import { useMerchant } from "@/hooks/useMerchant";
import { useTRPC } from "@/trpc/client";
import type { RentalsMerchantPortalRouterOutputs } from "@/trpc/client";
import { useQueryClient, useMutation } from "@tanstack/react-query";

type ApprovedBatch =
  RentalsMerchantPortalRouterOutputs["payoutBatches"]["approve"];

export function useApprovePayoutBatch(
  onSuccess?: (batch: ApprovedBatch) => void,
  onError?: (error: unknown) => void
) {
  const { merchant } = useMerchant();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const approveBatchMutation = useMutation({
    ...trpc.payoutBatches.approve.mutationOptions(),
    onSuccess: (batch, { rentalsMerchantId, id }) => {
      // seed the batch page so it doesn't first render a stale "pending" batch
      queryClient.setQueryData(
        trpc.payoutBatches.get.queryKey({ rentalsMerchantId, id }),
        batch
      );
      onSuccess?.(batch);
    },
    onError,
  });

  const approveBatch = async (batchId: string, otp?: string) => {
    if (!merchant?.id) return;

    await approveBatchMutation.mutateAsync(
      { rentalsMerchantId: merchant.id, id: batchId, otp },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: trpc.payoutBatches.list.queryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: trpc.payoutBatches.get.queryKey({
              rentalsMerchantId: merchant.id,
              id: batchId,
            }),
          });
          queryClient.invalidateQueries({
            queryKey: trpc.topUps.list.queryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: trpc.ledgerMovements.getBalance.queryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: trpc.ledgerMovements.list.queryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: trpc.payouts.list.queryKey({
              rentalsMerchantId: merchant.id,
              batchId,
            }),
          });
        },
      }
    );
  };

  return { approveBatch, isApproving: approveBatchMutation.isPending };
}
